import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export const CheckboxAndRadio: React.FC = () => {
  const { t } = useTranslation();
  const [selectAll, setSelectAll] = useState(false);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState('option1');

  const customRadioOptions = [
    { value: 'option1', label: t('Option 1: Basic package') },
    { value: 'option2', label: t('Option 2: Premium package') },
    { value: 'option3', label: t('Option 3: Ultimate package') }
  ];

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setCheckedItems(['a', 'b', 'c']);
    } else {
      setCheckedItems([]);
    }
  };

  const handleCheckboxChange = (name: string) => {
    setCheckedItems((prev) =>
      prev.includes(name)
        ? prev.filter((item) => item !== name)
        : [...prev, name]
    );
  };

  return (
    <div className="w-full flex flex-col gap-14">
      <div className="flex gap-10 items-center">
        <FormControlLabel
          className="!ml-0"
          control={
            <Checkbox
              className="primary-checkbox"
              checked={selectAll}
              indeterminate={checkedItems.length > 0 && checkedItems.length < 3}
              onChange={handleSelectAllChange}
            />
          }
          label={
            <span className="text-(--blue-4) text-base ml-1">
              {t('view_controls_select_all')}
            </span>
          }
        />

        <Checkbox
          size="small"
          className="primary-checkbox"
          checked={checkedItems.includes('a')}
          onChange={() => handleCheckboxChange('a')}
        />
        <Checkbox
          size="medium"
          className="primary-checkbox"
          checked={checkedItems.includes('b')}
          onChange={() => handleCheckboxChange('b')}
        />
        <Checkbox
          size="large"
          className="primary-checkbox"
          checked={checkedItems.includes('c')}
          onChange={() => handleCheckboxChange('c')}
        />
      </div>

      <div className="flex flex-col gap-6">
        <FormControlLabel
          className="!items-start"
          control={
            <Radio
              className="!py-0"
              checked={selectedOption === 'reservable'}
              onChange={handleRadioChange}
              value="reservable"
              color="primary"
            />
          }
          label={
            <Box>
              <p className="font-semibold text-sm text-(--dark-2)">
                {t('services_dialog_content_radio_reservable_title')}
              </p>
              <p className="text-[rgba(35,35,35,0.60)] text-[12px] mt-1">
                {t('services_dialog_content_radio_reservable_desc')}
              </p>
            </Box>
          }
        />

        <RadioGroup
          aria-labelledby="custom-radio-group"
          name="custom-radio"
          value={selectedOption}
          onChange={handleRadioChange}
        >
          {customRadioOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio color="primary" />}
              label={
                <span className="lg:text-base text-[13px] text-(--dark-2)">
                  {option.label}
                </span>
              }
            />
          ))}
        </RadioGroup>

        <Button
          variant="outlined"
          color="primary"
          onClick={() =>
            console.log(
              'Selected radio:',
              selectedOption,
              'Checked:',
              checkedItems
            )
          }
        >
          Log Selected Values
        </Button>
      </div>

      {/* Disabled states */}
      <div className="flex flex-col gap-4">
        <p className="font-semibold text-gray-700">Disabled</p>
        <div className="flex items-center gap-8">
          <FormControlLabel
            className="!ml-0"
            control={<Checkbox className="primary-checkbox" disabled />}
            label={<span className="text-(--dark-2) ml-1">Unchecked</span>}
          />
          <FormControlLabel
            className="!ml-0"
            control={
              <Checkbox className="primary-checkbox" disabled checked />
            }
            label={<span className="text-(--dark-2) ml-1">Checked</span>}
          />
          <FormControlLabel
            control={<Radio color="primary" disabled />}
            label={<span className="text-(--dark-2)">Radio</span>}
          />
          <FormControlLabel
            control={<Radio color="primary" disabled checked />}
            label={<span className="text-(--dark-2)">Radio checked</span>}
          />
        </div>
      </div>

      {/* Error / required state */}
      <div className="flex flex-col gap-4">
        <p className="font-semibold text-gray-700">Error / required</p>
        <FormControl error required>
          <FormControlLabel
            className="!ml-0"
            control={<Checkbox className="primary-checkbox" color="error" />}
            label={
              <span className="text-(--red-2) ml-1">
                {t('view_controls_select_all')}
              </span>
            }
          />
          <FormHelperText>This selection is required</FormHelperText>
        </FormControl>
      </div>
    </div>
  );
};

export default CheckboxAndRadio;
